import { useCallback, useEffect, useRef, useState } from "react";
import type { Content, StepDoc } from "@/app/queries";

export type Doc = { title: string; content: Content };
export type SaveStatus = "idle" | "scheduled" | "saving" | "saved" | "error";
export const AUTOSAVE_MS = 800;

type SaveBody = { title: string; content: Content };
type SaveFn = (body: SaveBody, cb: { onSuccess: (doc: StepDoc) => void; onError: (e: Error) => void }) => void;

/** Локальная копия документа + autosave через 800 мс после правки (A1).
 *  Серверная копия (с выданными id) принимается только если за время
 *  сохранения не было новых правок — иначе их бы перетёрло. */
export function useAutosave(server: StepDoc | undefined, save: SaveFn) {
  const [local, setLocal] = useState<Doc | null>(null);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const latest = useRef<Doc | null>(null);
  const edits = useRef(0);
  const savedEdits = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inflight = useRef<Promise<boolean> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (server && latest.current === null) {
      const d = { title: server.title, content: server.content };
      latest.current = d;
      setLocal(d);
    }
  }, [server]);

  const run = useCallback((): Promise<boolean> => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const doc = latest.current;
    if (!doc) return Promise.resolve(true);
    const at = edits.current;
    setStatus("saving");
    setError(null);
    const p = new Promise<boolean>((resolve) => {
      saveRef.current(
        { title: doc.title, content: doc.content },
        {
          onSuccess: (saved) => {
            savedEdits.current = at;
            if (edits.current === at) {
              const d = { title: saved.title, content: saved.content };
              latest.current = d;
              setLocal(d);
              setStatus("saved");
            }
            resolve(true);
          },
          onError: (e) => {
            setStatus("error");
            setError(e.message || "не удалось сохранить");
            resolve(false);
          },
        },
      );
    }).finally(() => {
      if (inflight.current === p) inflight.current = null;
    });
    inflight.current = p;
    return p;
  }, []);

  const update = useCallback(
    (fn: (d: Doc) => Doc) => {
      const cur = latest.current;
      if (!cur) return;
      edits.current++;
      const next = fn(cur);
      latest.current = next;
      setLocal(next);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void run(), AUTOSAVE_MS);
      setStatus("scheduled");
    },
    [run],
  );

  /** Досохранить всё, что ещё не ушло. true — на сервере актуальная копия. */
  const flush = useCallback(async (): Promise<boolean> => {
    if (timer.current) return run();
    if (inflight.current) await inflight.current;
    if (edits.current !== savedEdits.current) return run();
    return true;
  }, [run]);

  /** Принять документ с сервера (после импорта) как текущий. */
  const adopt = useCallback((doc: StepDoc) => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    savedEdits.current = edits.current;
    const d = { title: doc.title, content: doc.content };
    latest.current = d;
    setLocal(d);
    setStatus("saved");
    setError(null);
  }, []);

  // Уход со страницы с несохранённой правкой — отправить без ожидания.
  useEffect(
    () => () => {
      if (!timer.current) return;
      clearTimeout(timer.current);
      timer.current = null;
      const doc = latest.current;
      if (doc) saveRef.current({ title: doc.title, content: doc.content }, { onSuccess: () => {}, onError: () => {} });
    },
    [],
  );

  const unsaved = status === "scheduled" || status === "saving" || status === "error";
  return { local, status, error, unsaved, update, retry: run, flush, adopt };
}
